import { useState, type CSSProperties } from 'react';
import { PencilLineIcon, PlusIcon, Trash2Icon, TypeIcon } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { InstructionPopover, type InstructionScope } from './InstructionPopover';
import './document-canvas.css';

export type CanvasToolbarNouns = { singular: string; thisOne: string };

type CanvasSectionToolbarProps = {
  /** Bloc ciblé, exposé en `data-block-id` (même délai d'intention que la section). */
  blockId: string | null;
  canDelete: boolean;
  canEditBlock: boolean;
  canRewriteBlock: boolean;
  disabled: boolean;
  nouns: CanvasToolbarNouns;
  onBeginEdit: () => void;
  onDelete: () => void;
  onInsertAfter: (values: { instruction: string; title: string }) => void;
  /** Un popover ou une confirmation est ouvert : la barre ne doit pas disparaître. */
  onInteractionChange: (active: boolean) => void;
  onPointerEnter: () => void;
  onPointerLeave: () => void;
  onRewriteBlock: (instruction: string) => void;
  /** Consigne vide : la section est rédigée à nouveau. */
  onRewriteSection: (instruction: string) => void;
  /** Section ciblée, exposée en `data-section-id` : la cible bascule avec un délai d'intention au survol. */
  sectionId: string;
  sectionTitle: string;
  style: CSSProperties;
};

// Barre opaque sombre : contraste maximal au-dessus du document, quel que soit son thème.
const TOOLBAR_CLASS = [
  'canvas-toolbar-in absolute z-10 flex w-max max-w-[calc(100%-1rem)] flex-wrap items-center gap-0.5',
  'rounded-xl border border-white/10 bg-neutral-950 p-1 text-white shadow-[0_8px_24px_rgba(15,23,42,0.24)]',
].join(' ');
const TRIGGER_CLASS = [
  'h-7 gap-1.5 rounded-lg px-2 text-xs font-medium text-white/90',
  'hover:bg-white/12 hover:text-white dark:hover:bg-white/12 aria-expanded:bg-white/12 aria-expanded:text-white',
  'focus-visible:ring-white/40 disabled:opacity-40',
].join(' ');

function capitalize(text: string) {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function sectionRewriteScope(nouns: CanvasToolbarNouns): InstructionScope {
  return {
    description: `Laissez la consigne vide pour rédiger ${nouns.thisOne} à nouveau.`,
    instructionPlaceholder: 'Plus concis, ajouter des chiffres, ton plus direct…',
    instructionRequired: false,
    label: capitalize(nouns.thisOne),
    value: 'section',
  };
}

const BLOCK_REWRITE_SCOPE: InstructionScope = {
  description: 'L’assistant réécrit uniquement le passage sélectionné.',
  instructionPlaceholder: 'Plus court, plus formel, avec un exemple…',
  instructionRequired: true,
  label: 'Ce passage',
  value: 'block',
};

export function CanvasSectionToolbar({
  blockId,
  canDelete,
  canEditBlock,
  canRewriteBlock,
  disabled,
  nouns,
  onBeginEdit,
  onDelete,
  onInsertAfter,
  onInteractionChange,
  onPointerEnter,
  onPointerLeave,
  onRewriteBlock,
  onRewriteSection,
  sectionId,
  sectionTitle,
  style,
}: CanvasSectionToolbarProps) {
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  return (
    <div
      aria-label={`Actions sur ${nouns.thisOne}`}
      className={TOOLBAR_CLASS}
      data-block-id={blockId ?? undefined}
      data-section-id={sectionId}
      data-testid="canvas-section-toolbar"
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      role="toolbar"
      style={style}
    >
      <InstructionPopover
        disabled={disabled}
        instructionLabel="Consigne"
        instructionPlaceholder=""
        onOpenChange={onInteractionChange}
        onSubmit={({ instruction, scope }) => (
          scope === BLOCK_REWRITE_SCOPE.value ? onRewriteBlock(instruction) : onRewriteSection(instruction)
        )}
        // Une portée par cible : depuis un passage, on réécrit ce passage ; la section se réécrit
        // depuis sa propre cible (titre, marges), sans onglet qui laisse croire à un autre périmètre.
        scopes={canRewriteBlock ? [BLOCK_REWRITE_SCOPE] : [sectionRewriteScope(nouns)]}
        submitLabel="Réécrire"
        title="Réécrire avec l’assistant"
        trigger={(
          <Button className={TRIGGER_CLASS} disabled={disabled} size="xs" type="button" variant="ghost">
            <PencilLineIcon aria-hidden />
            Réécrire…
          </Button>
        )}
      />
      {canEditBlock ? (
        <Button className={TRIGGER_CLASS} disabled={disabled} onClick={onBeginEdit} size="xs" type="button" variant="ghost">
          <TypeIcon aria-hidden />
          Modifier
        </Button>
      ) : null}
      <InstructionPopover
        description={`La nouvelle ${nouns.singular} sera rédigée par l’assistant, juste après celle-ci.`}
        disabled={disabled}
        instructionLabel="Contenu attendu"
        instructionPlaceholder="Ce que la nouvelle partie doit couvrir"
        onOpenChange={onInteractionChange}
        onSubmit={onInsertAfter}
        submitLabel="Insérer"
        title={`Insérer une ${nouns.singular} après`}
        titleLabel="Titre"
        titlePlaceholder="Ex. Risques et points de vigilance"
        trigger={(
          <Button className={TRIGGER_CLASS} disabled={disabled} size="xs" type="button" variant="ghost">
            <PlusIcon aria-hidden />
            Insérer après…
          </Button>
        )}
        withTitle
      />
      <span aria-hidden className="mx-0.5 h-4 w-px bg-white/15" />
      <Button
        className={cn(TRIGGER_CLASS, 'text-red-400 hover:bg-red-500/15 hover:text-red-300 dark:hover:bg-red-500/15')}
        disabled={disabled || !canDelete}
        onClick={() => {
          setIsDeleteOpen(true);
          onInteractionChange(true);
        }}
        size="xs"
        title={canDelete ? undefined : `Le document doit garder au moins une ${nouns.singular}.`}
        type="button"
        variant="ghost"
      >
        <Trash2Icon aria-hidden />
        Supprimer
      </Button>

      <AlertDialog
        onOpenChange={(open) => {
          setIsDeleteOpen(open);
          onInteractionChange(open);
        }}
        open={isDeleteOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer {nouns.thisOne} ?</AlertDialogTitle>
            <AlertDialogDescription>
              « {sectionTitle} » sera retirée d’une nouvelle version du document. Vous pourrez toujours
              revenir à la version précédente depuis l’historique.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete} variant="destructive">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
